import type { Session } from "@supabase/supabase-js";
import { Gauge, Loader2, Menu, RefreshCcw, ShieldCheck, UserCog, Wallet, Wrench } from "lucide-react";
import { useEffect, useRef, useState, type ComponentType, type ReactNode, type SVGProps } from "react";

import type { MessageKey, SupportedLocale, Translate } from "../i18n";
import { BRAND } from "../lib/brand";
import { useDashboardRefresh } from "../lib/dashboard-refresh";
import { usePeekDashboardResource } from "../lib/dashboard-resource";
import {
  PROVIDER_DISPLAY_STATE_LABEL_KEYS,
  providerChipTone,
  type ProviderDisplayState,
  type ProviderChipTone
} from "../lib/provider-display-state";
import { setConsoleDocumentTitle } from "../lib/seo";
import type { ProviderOffer } from "../lib/tinkr-api";

/**
 * Provider console chrome (ADR-W-E1): state-aware navigation (PUX-15), the
 * mobile bottom tab bar (PUX-16), drawer focus management and skip link
 * (PUX-23), localized per-section titles (PUX-24) and the single refresh
 * control (PUX-13). Pure layout helpers are exported for tests.
 */

export type ProviderNavStage = "setup" | "approved" | "minimal";

type NavLike = { id: string; labelKey: MessageKey };

export type ProviderNavGroup<T extends NavLike> = {
  groupKey?: MessageKey;
  items: Array<T & { badge?: ReactNode }>;
};

/** Label overrides for the simplified navigation. */
const NAV_LABELS: Partial<Record<string, MessageKey>> = {
  dashboard: "providerShell.nav.home"
};

/**
 * Before approval: Home, Setup, Account. After approval: Home, Work queue
 * (with its offer badge), Shop setup, Payouts, Account; verification and
 * integrations move under Account. Single-item groups lose their heading.
 * Locked states (loading, unavailable, suspended, closed) keep Home, which
 * explains the state, and Account.
 */
export function buildProviderNavGroups<T extends NavLike>(
  items: T[],
  stage: ProviderNavStage,
  offerBadge: ReactNode
): ProviderNavGroup<T>[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  const pick = (id: string, labelKey?: MessageKey, badge?: ReactNode) => {
    const item = byId.get(id);
    if (!item) return null;
    return { ...item, labelKey: labelKey ?? NAV_LABELS[id] ?? item.labelKey, badge: badge ?? null };
  };
  const group = (groupKey: MessageKey | undefined, ...entries: Array<ReturnType<typeof pick>>): ProviderNavGroup<T> | null => {
    const present = entries.filter((entry): entry is NonNullable<typeof entry> => entry !== null);
    if (present.length === 0) return null;
    return { groupKey: present.length > 1 ? groupKey : undefined, items: present };
  };
  const groups: Array<ProviderNavGroup<T> | null> =
    stage === "approved"
      ? [
          group(undefined, pick("dashboard"), pick("work_queue", undefined, offerBadge)),
          group("stitch.nav.group.shopSetup", pick("capabilities"), pick("rate_cards"), pick("operations")),
          group(undefined, pick("payouts")),
          group(undefined, pick("account"))
        ]
      : stage === "setup"
        ? [group(undefined, pick("dashboard"), pick("onboarding", "providerShell.nav.setup"), pick("account"))]
        : [group(undefined, pick("dashboard"), pick("account"))];
  return groups.filter((entry): entry is ProviderNavGroup<T> => entry !== null);
}

/** Sections reachable by URL or in-panel links that have no nav entry of
 * their own highlight their parent instead of nothing. */
export function providerNavHighlight(sectionId: string, stage: ProviderNavStage): string {
  if (stage === "setup" && ["onboarding", "capabilities", "rate_cards", "operations", "api_keys"].includes(sectionId)) {
    return "onboarding";
  }
  if (stage === "approved" && (sectionId === "onboarding" || sectionId === "api_keys")) return "account";
  return sectionId;
}

export type ProviderChip = { tone: ProviderChipTone; label: string };

export function providerChip(display: ProviderDisplayState, t: Translate): ProviderChip | null {
  if (display.kind === "loading") return null;
  const tone = providerChipTone(display.kind);
  if (!tone) return null;
  return { tone, label: t(PROVIDER_DISPLAY_STATE_LABEL_KEYS[display.kind]) };
}

/**
 * Live pending-offer count from data Home or the queue already loaded. It
 * never fetches; expired offers are not counted.
 */
export function usePendingOfferCount(session: Session | null, enabled: boolean): number | null {
  const uid = session?.user?.id ?? "anon";
  type Cached = { offers: ProviderOffer[]; nextCursor?: string | null };
  const home = usePeekDashboardResource<Cached>("provider-offers:home:pending:" + uid);
  const queue = usePeekDashboardResource<Cached>("provider-offers:pending:" + uid);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, [enabled]);
  if (!enabled) return null;
  const freshest = home.fetchedAt >= queue.fetchedAt ? home.data ?? queue.data : queue.data ?? home.data;
  if (!freshest) return null;
  return freshest.offers.filter((offer) => {
    const expires = new Date(offer.expiresAt).getTime();
    return !Number.isFinite(expires) || expires > now;
  }).length;
}

/**
 * The waiting-offer count as a leaf: it subscribes to the offers cache and
 * re-renders on every offers update and once a minute, so it must not live in
 * the console shell, whose re-render would repaint the whole page.
 * `variant="nav"` is the sidebar pill; `variant="tab"` the bottom-bar dot.
 */
export function PendingOfferBadge({
  enabled,
  session,
  t,
  variant
}: {
  enabled: boolean;
  session: Session | null;
  t: Translate;
  variant: "nav" | "tab";
}) {
  const count = usePendingOfferCount(session, enabled);
  if (!count) return null;
  const shown = count > 99 ? "99+" : String(count);
  const label = <span className="sr-only">{t("providerShell.nav.offersWaiting").replace("{count}", String(count))}</span>;
  return variant === "nav" ? (
    <span className="stitch-nav-badge">
      <span aria-hidden="true">{shown}</span>
      {label}
    </span>
  ) : (
    <>
      <span aria-hidden="true" className="provider-tabbar-badge">{shown}</span>
      {label}
    </>
  );
}

/** Leaf for the document title, for the same reason as PendingOfferBadge. */
export function ConsoleDocumentTitle({
  enabled,
  sectionLabel,
  session
}: {
  enabled: boolean;
  sectionLabel: string;
  session: Session | null;
}) {
  useConsoleDocumentTitle(enabled, sectionLabel, usePendingOfferCount(session, enabled));
  return null;
}

/** "Work queue · Tinkr", prefixed "(3)" while offers wait (PUX-24). */
export function useConsoleDocumentTitle(enabled: boolean, sectionLabel: string, pendingOffers: number | null): void {
  useEffect(() => {
    if (!enabled) return;
    const base = `${sectionLabel} · ${BRAND.name}`;
    setConsoleDocumentTitle(pendingOffers && pendingOffers > 0 ? `(${pendingOffers}) ${base}` : base);
  }, [enabled, sectionLabel, pendingOffers]);
  useEffect(() => {
    if (!enabled) return;
    return () => setConsoleDocumentTitle(null);
  }, [enabled]);
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Mobile drawer as a modal (PUX-23): focus moves into the drawer, Tab stays
 * inside it, the page behind is inert, and focus returns to the toggle when
 * it closes.
 */
export function useDrawerFocusManagement(drawerOpen: boolean, onClose: () => void): (node: HTMLElement | null) => void {
  const drawerRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const drawer = drawerRef.current;
    if (!drawerOpen || !drawer) {
      if (wasOpen.current) {
        wasOpen.current = false;
        document.querySelector<HTMLElement>(".stitch-hamburger")?.focus();
      }
      return;
    }
    wasOpen.current = true;
    // The page behind the open drawer: content and footer. The bottom tab
    // bar stays live so its Menu button can close the drawer again.
    const targets = [...document.querySelectorAll<HTMLElement>(DRAWER_INERT_TARGETS)];
    for (const node of targets) node.inert = true;
    const focusables = () => [...drawer.querySelectorAll<HTMLElement>(FOCUSABLE)];
    focusables()[0]?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const list = focusables();
      if (list.length === 0) return;
      const head = list[0]!;
      const tail = list[list.length - 1]!;
      const inside = drawer.contains(document.activeElement);
      if (event.shiftKey && (!inside || document.activeElement === head)) {
        event.preventDefault();
        tail.focus();
      } else if (!event.shiftKey && (!inside || document.activeElement === tail)) {
        event.preventDefault();
        head.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    // The drawer only exists under 1024 px. Widening the window with it open
    // (rotation, a resized tablet window) closes it, so the page behind it
    // never stays inert next to the permanent sidebar.
    const wide = typeof window.matchMedia === "function" ? window.matchMedia(DRAWER_HIDDEN_QUERY) : null;
    const onWide = () => {
      if (wide?.matches) onCloseRef.current();
    };
    wide?.addEventListener?.("change", onWide);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      wide?.removeEventListener?.("change", onWide);
      for (const node of targets) node.inert = false;
    };
  }, [drawerOpen]);
  return (node) => {
    drawerRef.current = node;
  };
}

const DRAWER_INERT_TARGETS = "#stitch-main, .stitch-footer";
/** Matches stitch.css: the side navigation is a drawer below 1024 px. */
const DRAWER_HIDDEN_QUERY = "(min-width: 1024px)";

export function SkipToContentLink({ t }: { t: Translate }) {
  return (
    <a className="stitch-skip-link" href="#stitch-main">
      {t("providerShell.skipToContent")}
    </a>
  );
}

type BottomTab = {
  id: string;
  labelKey: MessageKey;
  icon: ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;
  badge?: ReactNode;
};

/** Phone/tablet tab bar under 1024 px (PUX-16): the daily screens one tap
 * away, the rest behind Menu. */
export function ProviderBottomTabBar({
  activeSectionId,
  drawerOpen,
  offerBadge,
  onOpenMenu,
  onSelectSection,
  prefetchSection,
  stage,
  t
}: {
  activeSectionId: string;
  drawerOpen: boolean;
  /** A PendingOfferBadge (variant "tab"), rendered beside the Queue icon. */
  offerBadge: ReactNode;
  onOpenMenu: () => void;
  onSelectSection: (sectionId: string) => void;
  /** Starts a section's chunks on hover, focus or touch (P-07), like the side nav. */
  prefetchSection?: (sectionId: string) => void;
  stage: ProviderNavStage;
  t: Translate;
}) {
  const tabs: BottomTab[] =
    stage === "approved"
      ? [
          { id: "dashboard", labelKey: "providerShell.nav.home", icon: Gauge },
          { id: "work_queue", labelKey: "providerShell.nav.queue", icon: Wrench, badge: offerBadge },
          { id: "payouts", labelKey: "stitch.nav.provider.payouts", icon: Wallet }
        ]
      : stage === "setup"
        ? [
            { id: "dashboard", labelKey: "providerShell.nav.home", icon: Gauge },
            { id: "onboarding", labelKey: "providerShell.nav.setup", icon: ShieldCheck },
            { id: "account", labelKey: "stitch.nav.account", icon: UserCog }
          ]
        : [
            { id: "dashboard", labelKey: "providerShell.nav.home", icon: Gauge },
            { id: "account", labelKey: "stitch.nav.account", icon: UserCog }
          ];
  const highlighted = providerNavHighlight(activeSectionId, stage);
  return (
    <nav aria-label={t("providerShell.nav.mobileLabel")} className="provider-tabbar" data-testid="provider-tabbar">
      {tabs.map((tab) => {
        const active = !drawerOpen && highlighted === tab.id;
        return (
          <button
            aria-current={active ? "page" : undefined}
            className="provider-tabbar-item"
            key={tab.id}
            onClick={() => onSelectSection(tab.id)}
            onFocus={prefetchSection ? () => prefetchSection(tab.id) : undefined}
            onPointerEnter={prefetchSection ? () => prefetchSection(tab.id) : undefined}
            onTouchStart={prefetchSection ? () => prefetchSection(tab.id) : undefined}
            type="button"
          >
            <span aria-hidden="true" className="provider-tabbar-icon">
              <tab.icon size={20} />
            </span>
            <span className="provider-tabbar-label">{t(tab.labelKey)}</span>
            {/* After the label, so the button is named "Queue, offers
                waiting: n" (the badge is positioned over the icon). */}
            {tab.badge ?? null}
          </button>
        );
      })}
      <button
        aria-expanded={drawerOpen}
        className="provider-tabbar-item"
        data-active={drawerOpen ? "true" : undefined}
        onClick={onOpenMenu}
        type="button"
      >
        <span aria-hidden="true" className="provider-tabbar-icon">
          <Menu size={20} />
        </span>
        <span className="provider-tabbar-label">{t("providerShell.nav.menu")}</span>
      </button>
    </nav>
  );
}

/** The one refresh control (PUX-13): a top-bar icon whose tooltip says when
 * the dashboard was last refreshed. Mobile keeps pull-to-refresh too. */
export function DashboardRefreshButton({ locale, t }: { locale: SupportedLocale; t: Translate }) {
  const { refreshing, triggerRefresh } = useDashboardRefresh();
  const [updatedAt, setUpdatedAt] = useState(() => Date.now());
  const wasRefreshing = useRef(refreshing);
  useEffect(() => {
    if (wasRefreshing.current && !refreshing) setUpdatedAt(Date.now());
    wasRefreshing.current = refreshing;
  }, [refreshing]);
  let updated = "";
  try {
    updated = new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(new Date(updatedAt));
  } catch {
    updated = "";
  }
  const label = refreshing ? t("dashboard.refreshing") : t("dashboard.refresh");
  const title = refreshing ? label : `${label} · ${t("providerShell.refreshUpdated").replace("{time}", updated)}`;
  return (
    <button
      aria-label={title}
      className="stitch-icon-button provider-refresh-button"
      data-testid="dashboard-refresh-button"
      disabled={refreshing}
      onClick={() => triggerRefresh()}
      title={title}
      type="button"
    >
      {refreshing ? <Loader2 aria-hidden className="spin" size={18} /> : <RefreshCcw aria-hidden size={18} />}
    </button>
  );
}
